import {AsyncPipe, CommonModule} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import {FormControl} from '@angular/forms';
import {MatIconModule} from '@angular/material/icon';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {select, Store} from '@ngrx/store';
import {map, Observable} from 'rxjs';
import {TrackerMetric} from 'src/app/core/model/tracker';
import {UpdateCurrentSkedValueChosen} from 'src/app/core/store/current-sked/current-sked.action';
import {selectCurrentSkedValueChosenMap} from 'src/app/core/store/current-sked/current-sked.selector';
import {TrackerMetricTypes} from 'src/app/trackers/shared/tracker-metric-types';
import {TrackerDetailMetricSelectComponent} from 'src/app/trackers/tracker-detail/metrics/metric/select/tracker-detail-metric-select.component';
import {OphDurationInputComponent} from '../../design/oph-duration-input/oph-duration-input.component';
import {OphInputOrangeComponent} from '../../design/oph-inputs/oph-input-orange/oph-input-orange.component';

import {OphButtonGroupComponent, OphButtonGroupOption} from '../../design/oph-button-group/oph-button-group.component';
import {MatTooltipModule} from '@angular/material/tooltip';

@Component({
  selector: 'tracker-metric-value-update',
  standalone: true,
  imports: [
    OphInputOrangeComponent,
    TrackerDetailMetricSelectComponent,
    MatIconModule,
    MatProgressSpinnerModule,
    AsyncPipe,
    CommonModule,
    OphDurationInputComponent,
    OphButtonGroupComponent,
    MatTooltipModule,
  ],
  templateUrl: './tracker-metric-value-update.component.html',
  styleUrl: './tracker-metric-value-update.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrackerMetricValueUpdateComponent implements OnChanges {
  @Input() metric: TrackerMetric;
  @Input() subtaskId: string; // if there is a subtaskId, this is used on the current sked. if not, used in tracker detail
  @Input() control: FormControl;
  @Input() inputStyle: Object;
  @Input() options: string[];
  @Input() selectStyle: Object;
  @Input() selectButtonStyle: Object;
  @Input() toggleWidth: string;
  @Input() showNumericUpdateButton: boolean;
  @Input() loadingStatus: string; // loading, success, null
  @Input() initiallyEmpty: boolean;
  @Input() booleanButtonStyle: Object;
  @Input() buttonStyle: Object;
  @Input() suffix: string;

  @Output() updateValue = new EventEmitter<string>();

  valueChosenMap$: Observable<Record<string, boolean>>;
  valueChosen$: Observable<boolean>;
  inputFocused = false;
  durationFocused = false;

  trackerMetricTypes = TrackerMetricTypes;
  booleanButtonGroupOptions = [
    {value: '0', display: 'No'},
    {value: '1', display: 'Yes'},
  ];

  // Validation error message signal
  validationError = signal<string | null>(null);

  get booleanOptions(): OphButtonGroupOption[] {
    const currentValue = this.control?.value;
    const baseOptions = [
      {name: '1', display: 'Yes'},
      {name: '0', display: 'No'},
    ];

    // Only include "Unset" option if current value is null/undefined
    if (currentValue === null || currentValue === undefined || currentValue === '') {
      return [
        {name: '1', display: 'Yes'},
        {name: null, display: 'Unset'},
        {name: '0', display: 'No'},
      ];
    }

    return baseOptions;
  }
  defaultInputStyle = {
    height: '40px',
    'font-size': '16px',
    'font-weight': '400',
    color: 'var(--brown-med)',
  };

  constructor(private store$: Store) {
    this.valueChosenMap$ = this.store$.pipe(select(selectCurrentSkedValueChosenMap));
    this.valueChosen$ = this.observeChosenMap();
    this.inputStyle = this.defaultInputStyle;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('inputStyle' in changes) {
      this.updateInputStyle();
    }

    // Validate when control or metric changes
    if (changes['control'] || changes['metric']) {
      this.validateValue();

      // Subscribe to control value changes to re-validate
      if (changes['control'] && this.control) {
        this.control.valueChanges.subscribe(() => {
          this.validateValue();
        });
      }
    }
  }

  private validateValue(): void {
    if (!this.control || !this.metric || this.metric.type !== this.trackerMetricTypes.Numeric) {
      this.validationError.set(null);
      return;
    }

    const value = this.control.value;
    if (value === null || value === undefined || value === '') {
      this.validationError.set(null);
      return;
    }

    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      this.validationError.set(null);
      return;
    }

    if (this.metric.min !== undefined && this.metric.min !== null && this.metric.min !== '') {
      const minValue = parseFloat(this.metric.min);
      if (!isNaN(minValue) && numValue < minValue) {
        this.validationError.set(`min: ${this.metric.min}`);
        return;
      }
    }

    if (this.metric.max !== undefined && this.metric.max !== null && this.metric.max !== '') {
      const maxValue = parseFloat(this.metric.max);
      if (!isNaN(maxValue) && numValue > maxValue) {
        this.validationError.set(`max: ${this.metric.max}`);
        return;
      }
    }

    this.validationError.set(null);
  }

  private updateInputStyle(): void {
    this.inputStyle = {
      ...this.defaultInputStyle,
      ...this.inputStyle,
      ...(this.suffix && {
        'border-top-right-radius': '0',
        'border-bottom-right-radius': '0',
        'border-right': 'none',
      }),
    };
  }

  observeChosenMap(): Observable<boolean> {
    return this.valueChosenMap$.pipe(
      map(chosenMap => {
        if (this.initiallyEmpty && !chosenMap[this.subtaskId]) {
          this.control.setValue('');
        }
        if (this.subtaskId) {
          return chosenMap[this.subtaskId];
        } else {
          return true;
        }
      })
    );
  }

  onUpdateValue(value: string) {
    this.control.setValue(value);
    this.validateValue();

    // Don't emit update if there's a validation error
    if (this.validationError()) {
      return;
    }

    this.updateValue.emit(value);
    this.store$.dispatch(new UpdateCurrentSkedValueChosen({id: this.subtaskId}));
  }

  onInputFocus() {
    this.inputFocused = true;
  }

  onInputBlur() {
    this.inputFocused = false;
    this.inputStyle = {...this.defaultInputStyle, ...this.inputStyle};
  }

  onDurationFocus() {
    this.durationFocused = true;
  }

  onDurationBlur() {
    this.durationFocused = false;
  }

  onDurationUpdate() {
    if (this.loadingStatus) {
      return;
    }
    this.updateValue.emit(this.control.value);
  }

  onPhotoError() {
    // Photo failed to load - could add error handling here if needed
    console.warn('Failed to load photo for metric:', this.metric?.name);
  }

  getBooleanSelectedValue(): string | null {
    const value = this.control?.value;
    if (value === '1') return '1';
    if (value === '0') return '0';
    return null;
  }
}
